/**
 * Lanczos-3 resampling of RGBA pixels, for the big reductions of the image
 * pipeline (an avatar crop of 2048 px down to 512). The canvas's own
 * smoothing, even in halving steps, does not low-pass enough: a fine
 * pattern (a checked shirt, a fence, a screen) comes out as moiré, a false
 * pattern that is not in the photo. Here the kernel is stretched by the
 * reduction factor, so every output pixel is a proper weighted average of
 * the source pixels it covers, as in sharp/libvips and Pillow.
 *
 * Separable (rows, then columns), with the weights computed once per
 * output column and row. Colour is premultiplied by alpha while filtering,
 * so transparent pixels never bleed a dark fringe into the edges. Pure
 * arithmetic on typed arrays: it runs in the image worker and is unit
 * tested without a browser.
 */

const LOBES = 3;

function lanczos(x: number): number {
  if (x === 0) return 1;
  if (x <= -LOBES || x >= LOBES) return 0;
  const px = Math.PI * x;
  return (LOBES * Math.sin(px) * Math.sin(px / LOBES)) / (px * px);
}

interface Taps {
  /** First source index of each output pixel's window. */
  start: Int32Array;
  /** How many source pixels the window holds. */
  count: Int32Array;
  /** `stride` weights per output pixel, normalised to sum to 1. */
  weights: Float32Array;
  stride: number;
}

function taps(inSize: number, outSize: number): Taps {
  const scale = inSize / outSize;
  const stretch = Math.max(1, scale);
  const support = LOBES * stretch;
  const stride = Math.ceil(support) * 2 + 2;
  const start = new Int32Array(outSize);
  const count = new Int32Array(outSize);
  const weights = new Float32Array(outSize * stride);
  for (let i = 0; i < outSize; i++) {
    const center = (i + 0.5) * scale;
    const lo = Math.max(0, Math.floor(center - support));
    const hi = Math.min(inSize, Math.ceil(center + support));
    const n = Math.min(stride, hi - lo);
    let sum = 0;
    for (let j = 0; j < n; j++) {
      const w = lanczos((lo + j + 0.5 - center) / stretch);
      weights[i * stride + j] = w;
      sum += w;
    }
    if (sum !== 0) for (let j = 0; j < n; j++) weights[i * stride + j]! /= sum;
    start[i] = lo;
    count[i] = n;
  }
  return { start, count, weights, stride };
}

/** Whether every pixel is opaque (then there is nothing to premultiply). */
function opaque(px: Uint8ClampedArray): boolean {
  for (let i = 3; i < px.length; i += 4) if (px[i] !== 255) return false;
  return true;
}

/**
 * Resamples `src` (`sw` x `sh` RGBA, straight alpha, as ImageData holds
 * it) to `dw` x `dh`. Meant for reductions; it also works for enlargements.
 */
export function resampleRGBA(
  src: Uint8ClampedArray,
  sw: number,
  sh: number,
  dw: number,
  dh: number,
): Uint8ClampedArray<ArrayBuffer> {
  if (src.length < sw * sh * 4) throw new RangeError('pixel data is shorter than its size');
  const solid = opaque(src);
  // Rows first: sw x sh -> dw x sh, premultiplied, as floats.
  const h = taps(sw, dw);
  const mid = new Float32Array(dw * sh * 4);
  for (let y = 0; y < sh; y++) {
    const row = y * sw * 4;
    const outRow = y * dw * 4;
    for (let x = 0; x < dw; x++) {
      const n = h.count[x]!;
      const w0 = x * h.stride;
      let p = row + h.start[x]! * 4;
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      if (solid) {
        for (let j = 0; j < n; j++, p += 4) {
          const w = h.weights[w0 + j]!;
          r += w * src[p]!;
          g += w * src[p + 1]!;
          b += w * src[p + 2]!;
        }
        a = 255;
      } else {
        for (let j = 0; j < n; j++, p += 4) {
          const w = h.weights[w0 + j]!;
          const wa = (w * src[p + 3]!) / 255;
          r += wa * src[p]!;
          g += wa * src[p + 1]!;
          b += wa * src[p + 2]!;
          a += w * src[p + 3]!;
        }
      }
      const o = outRow + x * 4;
      mid[o] = r;
      mid[o + 1] = g;
      mid[o + 2] = b;
      mid[o + 3] = a;
    }
  }
  // Then columns: dw x sh -> dw x dh, back to straight alpha.
  const v = taps(sh, dh);
  const out = new Uint8ClampedArray(dw * dh * 4);
  for (let y = 0; y < dh; y++) {
    const n = v.count[y]!;
    const w0 = y * v.stride;
    const first = v.start[y]!;
    for (let x = 0; x < dw; x++) {
      let p = (first * dw + x) * 4;
      const step = dw * 4;
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      for (let j = 0; j < n; j++, p += step) {
        const w = v.weights[w0 + j]!;
        r += w * mid[p]!;
        g += w * mid[p + 1]!;
        b += w * mid[p + 2]!;
        a += w * mid[p + 3]!;
      }
      const o = (y * dw + x) * 4;
      if (solid) {
        out[o] = r;
        out[o + 1] = g;
        out[o + 2] = b;
        out[o + 3] = 255;
      } else if (a > 0.5) {
        const k = 255 / a;
        out[o] = r * k;
        out[o + 1] = g * k;
        out[o + 2] = b * k;
        out[o + 3] = a;
      }
      // (fully transparent: stays 0, 0, 0, 0)
    }
  }
  return out;
}
